import FallbackImage from '@/images/placeholder.png'
import { camelCase, kebabCase } from 'change-case'
import {
  getAttributeItem,
  getAttributeItemsByGroup,
  getBrand,
  getFormattedColorAttributes,
  getFormattedSelectVariantAttributes,
  getFormattedFrameSizeAttributes,
  getFormattedUpgradeAttributes,
  getFrameSize,
  getFrameSizeCm,
  getProductSeason,
  getProductNameInternal,
  getFrameType,
  getWheelSizeInch,
  getProductVariantProperties,
  getSpecsAttributesFormatted,
  getSpecsAttributesFromVariants,
  isArchiveItem,
  mergeValuesToArrays,
  getArticleType,
  getCategory,
  getProductBidexCode,
  getPropOrAttrPaths,
} from '@/js/helper/aimeos-attribute-helper'
import { getMediaByType, getCompareMediaPreview } from '@/js/helper/aimeos-image-helper'
import {
  getTextByType,
  getTextByTypeRegex,
  getTextContent,
  sortTextByType,
} from '@/js/helper/aimeos-name-helper.js'
import API_CONSTANTS from '@/js/helper/api-constants.js'
import BIKE_ATTRIBUTES from '@/js/helper/bike-attributes.js'
import FILTER_CONSTANTS from '@/js/helper/filter-constants.js'
import IMAGE_CONSTANTS from '@/js/helper/image-constants.js'
import {
  getLinkAllowed,
  getLinkOfType,
  getLinkUrl,
  getSelfLink,
  hasAllowed,
} from '@/js/helper/links-helper.js'
import { getContentBaseurl } from '@/js/helper/meta-helper.js'
import productSearchAttributeScore from '@/js/helper/product-search-attribute-score.js'
// import { getIsSparePart } from '@/js/helper/product-spare-part-helper.js'
import { compareProductGroupConfig } from '@/js/helper/compare-products-helper.js'
import { getDataArray, getRelatedIncludes } from '@/js/helper/relationships-helper.js'
import {
  cloneDeep,
  compact,
  get,
  has,
  head,
  isArray,
  isEmpty,
  isUndefined,
  mergeWith,
  map,
  uniqWith,
  flatten,
  sortBy,
  unzip,
} from 'lodash-es'
import { getImageSize, getImageUrl, getPreviewImage } from './aimeos-image-helper'
import {
  DP_ID_TO_ROLE,
  COMPANY_CODE_TO_BRAND,
  CLIENT_TO_COMPANY_CODE,
  COMPANY_CODE_TO_OVERSELLING,
} from '@/js/helper/dp-usermanagement-constants.js'

const propsForSortAndFiltering = [
  API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.PRODUCT_SEQUENCE,
  API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.PRODUCT_VARIANTS_OUTLET,
  API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.PRODUCT_VARIANTS_CURRENT,
]

const attrsForSortAndFiltering = [
  BIKE_ATTRIBUTES.TYPE.PRODUCT_VARIANTS_CURRENT,
  BIKE_ATTRIBUTES.TYPE.PRODUCT_VARIANTS_OUTLET,
]

// Attributes
export const getCode = ({ attributes }) => {
  return get(attributes, API_CONSTANTS.AIMEOS.PRODUCTS.ATTRIBUTE.CODE, '')
}
export const getConfig = ({ attributes }) => {
  return get(attributes, API_CONSTANTS.AIMEOS.PRODUCTS.ATTRIBUTE.CONFIG)
}
export const getProductType = ({ attributes }) => {
  return get(attributes, API_CONSTANTS.AIMEOS.PRODUCTS.ATTRIBUTE.TYPE)
}
export const getProductId = ({ attributes }) => {
  return get(attributes, API_CONSTANTS.AIMEOS.PRODUCTS.ATTRIBUTE.ID)
}
export const getStatus = ({ attributes }) => {
  return get(attributes, API_CONSTANTS.AIMEOS.PRODUCTS.ATTRIBUTE.STATUS)
}
export const getLabel = ({ attributes }) => {
  return get(attributes, API_CONSTANTS.AIMEOS.PRODUCTS.ATTRIBUTE.LABEL, '')
}

/*********************
 *
 * Links
 *
 */

export const getLinkProps = ({ links }) => {
  const selfAllowedProp = selfAllowed({ links })
  return {
    selfUrl: selfUrl({ links }),
    selfAllowed: selfAllowedProp,
    hasSelfAllowed: hasSelfAllowed({ links }),
    allowedGet: allowedGet({ selfAllowed: selfAllowedProp }),
    allowedDelete: allowedDelete({ selfAllowed: selfAllowedProp }),
    allowedPatch: allowedPatch({ selfAllowed: selfAllowedProp }),
  }
}

export const selfUrl = ({ links }) => getLinkUrl(getSelfLink(links))

export const selfAllowed = ({ links }) => getLinkAllowed(getSelfLink(links))

export const hasSelfAllowed = ({ links }) => hasAllowed(selfAllowed({ links }))

export const allowedGet = ({ selfAllowed }) =>
  !hasSelfAllowed(selfAllowed) || !!selfAllowed.find(perm => perm === API_CONSTANTS.ALLOW.GET)

export const allowedDelete = ({ selfAllowed }) =>
  !hasSelfAllowed(selfAllowed) || !!selfAllowed.find(perm => perm === API_CONSTANTS.ALLOW.DELETE)

export const allowedPatch = ({ selfAllowed }) =>
  !hasSelfAllowed(selfAllowed) || !!selfAllowed.find(perm => perm === API_CONSTANTS.ALLOW.PATCH)

export const getBasketLink = ({ links }) => {
  return getLinkOfType(links, API_CONSTANTS.AIMEOS.LINK.BASKETPRODUCT)
}
export const allowedBasketPost = ({ basketLinkAllowed }) => {
  return !basketLinkAllowed || !!basketLinkAllowed.find(perm => perm === API_CONSTANTS.ALLOW.POST)
}

export const getBasketProps = ({ links }) => {
  const basketLink = getBasketLink({ links })
  const basketLinkAllowed = getLinkAllowed(basketLink)
  return {
    allowedBasketPost: allowedBasketPost({ basketLinkAllowed }),
    basketLinkUrl: getLinkUrl(basketLink),
  }
}

/*********************
 *
 * Related attributes/properties
 *
 */
export const getRelatedAttributes = ({ relationships, includedArray }) => {
  return getRelatedIncludes({
    relationships: relationships,
    included: includedArray,
    type: API_CONSTANTS.AIMEOS.TYPE.ATTRIBUTE,
  })
}
export const getRelatedProductProperties = ({ relationships, includedArray }) => {
  return getRelatedIncludes({
    relationships: relationships,
    included: includedArray,
    type: API_CONSTANTS.AIMEOS.TYPE.PRODUCT_PROPERTY,
  })
}

export const getRelatedCategories = ({ relationships, includedArray }) => {
  const categories = getRelatedIncludes({
    relationships: relationships,
    included: includedArray,
    type: API_CONSTANTS.AIMEOS.TYPE.CATALOG,
  })

  return categories.map(category => {
    return {
      code: get(category, API_CONSTANTS.AIMEOS.CATEGORY.PATHS.CODE),
      label: getName({
        texts: getTexts({ relationships: get(category, 'relationships', {}), includedArray }),
        attributes: get(category, 'attributes', {}),
      }),
    }
  })
}

export const getProductSpecsAttributesFormatted = ({ attributes, includedArray }) => {
  return getSpecsAttributesFormatted({
    attributes,
    included: includedArray,
  })
}

/*********************
 *
 * Texts
 *
 */

export const getTexts = ({ relationships, includedArray }) => {
  return getRelatedIncludes({
    relationships: relationships,
    included: includedArray,
    type: API_CONSTANTS.AIMEOS.TYPE.TEXT,
  })
}
export const getUspTexts = ({ texts }) => {
  return sortTextByType({
    texts: getTextByTypeRegex({
      regex: API_CONSTANTS.AIMEOS.TEXT.TYPE.DEALERUSP_REGEX,
      texts,
    }),
  })
}

// text: Name
export const getName = ({ texts, attributes }) => {
  // ToDo: Check for text type name?
  return nameText({ texts }) || getLabel({ attributes })
}
export const nameTextItems = ({ texts }) => {
  return getTextByType({ type: API_CONSTANTS.AIMEOS.TEXT.TYPE.NAME, texts })
}
export const firstNameTextItem = ({ texts }) => {
  return head(nameTextItems({ texts }))
}
export const nameText = ({ texts }) => {
  return getTextContent(firstNameTextItem({ texts }))
}

/*********************
 *
 * Prices
 *
 */

export const getPrices = ({ relationships, includedArray }) => {
  return getRelatedIncludes({
    relationships: relationships,
    included: includedArray,
    type: API_CONSTANTS.AIMEOS.TYPE.PRICE,
  })
}

export const relevantPricesSorted = ({
  prices,
  types = API_CONSTANTS.AIMEOS.PRICE.RELEVANT_PRICES,
}) => {
  return prices
    .filter(price => types.includes(get(price, API_CONSTANTS.AIMEOS.PRICE.PATHS.TYPE)))
    .sort(sortPrice())
}

export const sortPrice = (descending = false) => {
  return (a, b) => {
    const priceA =
      parseFloat(get(a, API_CONSTANTS.AIMEOS.PRICE.PATHS.VALUE), 0) +
      parseFloat(get(a, API_CONSTANTS.AIMEOS.PRICE.PATHS.COSTS), 0)
    const priceB =
      parseFloat(get(b, API_CONSTANTS.AIMEOS.PRICE.PATHS.VALUE), 0) +
      parseFloat(get(b, API_CONSTANTS.AIMEOS.PRICE.PATHS.COSTS), 0)
    return (priceA - priceB) * descending ? -1 : 1
  }
}

export const getPriceCurrency = ({ priceItem }) =>
  isUndefined(priceItem)
    ? 'EUR'
    : get(priceItem, API_CONSTANTS.AIMEOS.PRICE.PATHS.CURRENCYID, 'EUR')

export const getPriceValue = ({ priceItem }) => {
  if (isUndefined(priceItem)) {
    return undefined
  }
  const value = parseFloat(get(priceItem, API_CONSTANTS.AIMEOS.PRICE.PATHS.VALUE))
  const cost = parseFloat(get(priceItem, API_CONSTANTS.AIMEOS.PRICE.PATHS.COSTS))
  return value + cost
}
/*********************
 *
 * Media
 *
 */

export const getMedia = ({ relationships, includedArray }) => {
  return getRelatedIncludes({
    relationships: relationships,
    included: includedArray,
    type: API_CONSTANTS.AIMEOS.TYPE.MEDIA,
  }).sort((a, b) => {
    return (
      get(
        a,
        [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.AIMEOS.PRODUCTS.RELATIONSHIP.ATTRIBUTE.POSITION],
        999,
      ) -
      get(
        b,
        [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.AIMEOS.PRODUCTS.RELATIONSHIP.ATTRIBUTE.POSITION],
        999,
      )
    )
  })
}

export const getMediaImages = ({ media }) =>
  getMediaByType({
    media,
    type: API_CONSTANTS.AIMEOS.MEDIA.TYPE.DEFAULT,
    mimeTypeRegex: /^image/,
  })

export const getMediaDatasheets = ({ media }) =>
  getMediaByType({ media, type: API_CONSTANTS.AIMEOS.MEDIA.TYPE.DATASHEET })

export const getMediaThreeSixtyImages = ({ media }) =>
  getMediaByType({ media, type: API_CONSTANTS.AIMEOS.MEDIA.TYPE.THREESIXTY })

export const getDatasheets = ({ mediaDatasheets, contentBaseurl, name = '' }) => {
  return mediaDatasheets.map(datasheet => ({
    name,
    url: `${contentBaseurl}/${get(datasheet, API_CONSTANTS.AIMEOS.MEDIA.PATHS.URL)}`,
  }))
}

export const getMediaExplodedDrawing = ({ media, contentBaseurl }) =>
  getMediaByType({ media, type: API_CONSTANTS.AIMEOS.MEDIA.TYPE.EXPLODED_DRAWING }).map(
    item => `${contentBaseurl}/${get(item, API_CONSTANTS.AIMEOS.MEDIA.PATHS.URL)}`,
  )

/*********************
 *
 * Other
 *
 */

export const getProductSortAndFilteringProps = ({
  relatedProductProperties,
  relatedAttributes,
}) => {
  const relatedProductProps = relatedProductProperties
    .filter(include =>
      propsForSortAndFiltering.includes(
        get(include, API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.PATHS.TYPE),
      ),
    )
    .reduce((acc, prop) => {
      const type = get(prop, API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.PATHS.TYPE)
      const value =
        type === API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.PRODUCT_SEQUENCE
          ? parseInt(get(prop, API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.PATHS.VALUE, 999), 10)
          : !!parseInt(get(prop, API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.PATHS.VALUE, 0), 10)
      acc[type] = value
      return acc
    }, {})

  const relatedProductAttrs = relatedAttributes
    .filter(include =>
      attrsForSortAndFiltering.includes(get(include, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.TYPE)),
    )
    .reduce((acc, prop) => {
      const type = get(prop, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.TYPE)
      // console.log(prop, get(prop, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.ID))
      const value = !!parseInt(get(prop, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.ID, 0), 10)
      acc[type] = value
      return acc
    }, {})
  // console.log({ ...relatedProductAttrs, ...relatedProductProps })
  return { ...relatedProductAttrs, ...relatedProductProps }
}

export const getSearchHits = ({ relatedAttributes, currentSearch = {}, parentHits, code }) => {
  const searchAttributesRelated = Object.entries(currentSearch).reduce((acc, [key, value]) => {
    // code is not an attribute
    if (key === 'code') {
      return acc
    }
    const foundAttributes = flatten(value).map(v => {
      const idString = `${v}`
      const relatedAttribute = relatedAttributes.find(attr => attr.id === idString)
      return relatedAttribute
    }, {})
    const hits = compact(
      foundAttributes.map(a => (a ? get(a, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.CODE) : null)),
    )
    acc.push({
      key,
      hits,
      score: hits.length ? get(productSearchAttributeScore, key, 1) : 0,
      search: value,
    })

    return acc
  }, [])

  const searchAttributes = Array.isArray(parentHits)
    ? uniqWith(
        [
          ...parentHits.filter(
            h =>
              h.score > 0 &&
              !FILTER_CONSTANTS.DETAIL_PAGE_SEARCH_HITS_PARENT_EXCLUDES.includes(h.key),
          ),
          ...searchAttributesRelated,
        ],
        (sA, pH) => sA.key === pH.key,
      )
    : searchAttributesRelated

  // Add sku search hit
  if (currentSearch.code && currentSearch.code === code) {
    searchAttributes.push({
      key: 'code',
      hits: 1,
      score: get(productSearchAttributeScore, 'code', 1),
      search: [],
    })
  }

  const searchScore = searchAttributes.reduce((acc, { score }) => acc + score, 0)
  return {
    searchAttributes,
    searchScore,
  }
}

const shouldHideProduct = ({ firstPriceItem, archiveItem }) => {
  return archiveItem ? false : isUndefined(firstPriceItem)
}

const getBasketAttributes = ({ relatedAttributes }) => {
  return getAttributeItemsByGroup({
    attributes: relatedAttributes,
    group: BIKE_ATTRIBUTES.GROUP.BASKET,
  }).reduce((acc, attr) => {
    acc[get(attr, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.TYPE)] = get(
      attr,
      API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.ID,
    )
    return acc
  }, {})
}

const addCustomBasketAttributes = ({ baseData, brand }) => {
  const customBasketAttributes = {}

  const productOrderBikeBrandAttrId = get(baseData, [
    'basketAttributes',
    BIKE_ATTRIBUTES.TYPE.PRODUCT_ORDER_BIKE_BRAND,
  ])
  if (productOrderBikeBrandAttrId && brand) {
    customBasketAttributes[camelCase(BIKE_ATTRIBUTES.TYPE.PRODUCT_ORDER_BIKE_BRAND)] = {
      code: BIKE_ATTRIBUTES.TYPE.PRODUCT_ORDER_BIKE_BRAND,
      name: kebabCase(BIKE_ATTRIBUTES.TYPE.PRODUCT_ORDER_BIKE_BRAND),
      id: productOrderBikeBrandAttrId,
      value: brand,
    }
  }
  return customBasketAttributes
}

/*********************
 *
 * Variant product data
 *
 */

export const getRelatedProducts = ({
  relationships,
  includedArray,
  type = API_CONSTANTS.AIMEOS.TYPE.PRODUCT,
  itemType = API_CONSTANTS.AIMEOS.TYPE.PRODUCT,
  listType,
}) => {
  return getRelatedIncludes({
    relationships: relationships,
    included: includedArray,
    type,
    itemType,
    itemListType: listType,
  }).map(product => {
    const prop = head(
      getRelatedIncludes({
        relationships: product.relationships,
        included: includedArray,
        type: API_CONSTANTS.AIMEOS.TYPE.PRODUCT_PROPERTY,
      }).filter(
        include =>
          get(include, API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.PATHS.TYPE) ===
          API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.ITEM_SEQUENCE,
      ),
    )
    return {
      ...product,
      [API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.ITEM_SEQUENCE]: prop
        ? get(prop, API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.PATHS.VALUE, 999)
        : 999,
    }
  })
}

const sortRelatedProductsByItemSequence = ({ products }) =>
  [...products].sort((a, b) => {
    return (
      parseInt(a[API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.ITEM_SEQUENCE], 10) -
      parseInt(b[API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.ITEM_SEQUENCE], 10)
    )
  })

export const getVariantSpecsAttributesFormatted = ({ relatedProducts, includedArray }) => {
  return getSpecsAttributesFromVariants({
    variantProducts: relatedProducts,
    included: includedArray,
  })
}

export const getVariantType = ({ productVariantProperties }) => {
  // console.log({ productVariantProperties })
  const variantTypes = productVariantProperties.reduce((acc, prop) => {
    const paths = getPropOrAttrPaths(prop)
    const type = get(FILTER_CONSTANTS.PROP_TYPE_SCOPE, get(prop, paths.TYPE))

    if (!isUndefined(type)) {
      acc.push(type)
    }
    return acc
  }, [])

  if (variantTypes.length === 0) {
    // console.log('Outlet Type Fallback: current')
    variantTypes.push(
      FILTER_CONSTANTS.PROP_TYPE_SCOPE[
        API_CONSTANTS.AIMEOS.PRODUCT_PROPERTY.TYPES.PRODUCT_VARIANTS_CURRENT
      ],
    )
  }

  return variantTypes
}

export const getStockAttribute = ({ relatedAttributes }) => {
  return head(
    getAttributeItem({
      attributes: relatedAttributes,
      key: FILTER_CONSTANTS.TYPE.STOCK,
    }),
  )
}

export const getStockLabel = ({ relatedAttributes }) => {
  const stockAttribute = getStockAttribute({ relatedAttributes })
  return `${FILTER_CONSTANTS.TYPE.STOCK}: ${
    stockAttribute ? get(stockAttribute, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.LABEL) : 'N/A'
  }`
}

export const getProductItemSupplier = ({ relatedAttributes }) => {
  const attribute = head(
    getAttributeItem({
      attributes: relatedAttributes,
      key: BIKE_ATTRIBUTES.TYPE.ITEM_SUPPLIER,
    }),
  )
  return attribute ? get(attribute, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.CODE) : undefined
}

export const getProductItemType = ({ relatedAttributes }) => {
  // console.log(
  //   getAttributeItem({
  //     attributes: relatedAttributes,
  //     key: BIKE_ATTRIBUTES.TYPE.ITEM_TYPE,
  //   }),
  // )
  const attributes = getAttributeItem({
    attributes: relatedAttributes,
    key: BIKE_ATTRIBUTES.TYPE.ITEM_TYPE,
  })

  return attributes ? attributes.map(a => get(a, API_CONSTANTS.AIMEOS.ATTRIBUTE.PATHS.CODE)) : []
}

/*********************
 *
 * Generate product data
 *
 */

export const generateAdditionalVariantProductData = ({
  id,
  parentId,
  parentCode,
  parentName,
  parentBidexCode,
  type,
  links,
  attributes,
  relationships,
  includedArray,
  contentBaseurl,
  currentSearch = {},
  selectSearchAttributes,
  bikeBrand,
  addAllImagesWithDefaultSize,
  mergeColorAndFrameAttributes = false,
}) => {
  const relatedAttributes = getRelatedAttributes({ relationships, includedArray })
  const relatedProductProperties = getRelatedProductProperties({
    relationships,
    includedArray,
  })
  const relatedCategories = getRelatedCategories({ relationships, includedArray })

  const texts = getTexts({ relationships, includedArray })
  const media = getMedia({ relationships, includedArray })
  const mediaDatasheets = getMediaDatasheets({ media })
  const productVariantProperties = getProductVariantProperties({
    relatedProductProperties,
    relatedAttributes,
  })
  const variantType = getVariantType({ productVariantProperties })

  const baseData = generateBaseProductData({
    id,
    parentId,
    parentCode,
    parentName,
    parentBidexCode,
    type,
    links,
    attributes,
    relationships,
    relatedAttributes,
    includedArray,
    imageMinSize: IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_LIST,
    addAllImagesWithDefaultSize,
    contentBaseurl,
    media,
    texts,
    variantType,
    mergeColorAndFrameAttributes,
  })

  const frameSizeAttributes = getFormattedFrameSizeAttributes(relatedAttributes, includedArray)
  const frameSize = getFrameSize(relatedAttributes)
  const colorAttributes = getFormattedColorAttributes(relatedAttributes, includedArray)
  const upgradeAttributes = getFormattedUpgradeAttributes(
    relatedAttributes,
    includedArray,
    Object.keys(baseData?.groupPrices || {}),
  )
  const frameType = getFrameType(relatedAttributes, includedArray)
  const itemSupplier = getProductItemSupplier({ relatedAttributes })
  const itemType = getProductItemType({ relatedAttributes })
  const code = getCode({ attributes })
  const customBasketAttributes = addCustomBasketAttributes({
    baseData,
    brand: get(baseData, 'isSparepartArticleType') ? bikeBrand : get(baseData, 'brand'),
  })
  const specsAttributesFormatted = getProductSpecsAttributesFormatted({
    attributes: relatedAttributes,
    includedArray,
  })

  return {
    ...baseData,
    code,
    ...getBasketProps({ links }),
    datasheets: getDatasheets({ mediaDatasheets, contentBaseurl, name: baseData.name }),
    colorAttributes,
    hasColorAttributes: !isEmpty(colorAttributes),
    upgradeAttributes,
    frameSize,
    frameSizeAttributes,
    hasFrameSizeAttributes: !isEmpty(frameSizeAttributes),
    frameType,
    // frameTypeName: get(frameType, [
    //   BIKE_ATTRIBUTES.TYPE.ITEM_GENDER_FRAME,
    //   API_CONSTANTS.AIMEOS.ATTRIBUTE.ATTRIBUTE.NAME,
    // ]),
    frameTypeName: get(BIKE_ATTRIBUTES.FILTER_ICON_NAMES, [
      BIKE_ATTRIBUTES.TYPE.ITEM_GENDER_FRAME,
      get(frameType, [
        BIKE_ATTRIBUTES.TYPE.ITEM_GENDER_FRAME,
        API_CONSTANTS.AIMEOS.ATTRIBUTE.ATTRIBUTE.CODE,
      ]),
    ]),
    frameTypeIcon: get(BIKE_ATTRIBUTES.FILTER_ICONS, [
      BIKE_ATTRIBUTES.TYPE.ITEM_GENDER_FRAME,
      get(frameType, [
        BIKE_ATTRIBUTES.TYPE.ITEM_GENDER_FRAME,
        API_CONSTANTS.AIMEOS.ATTRIBUTE.ATTRIBUTE.CODE,
      ]),
    ]),
    variantType,
    ...getSearchHits({
      relatedAttributes,
      currentSearch,
      parentHits: get(selectSearchAttributes, 'searchAttributes', []),
      code,
    }),
    categories: relatedCategories,
    itemSupplier,
    itemType,
    customBasketAttributes,
    specsAttributesFormatted,
    // eslint-disable-next-line spaced-comment
/////////////////////////////////////
/////////////////////////////////////////////////////
//////////////////////////////////////////////
//////////////
  }
}

export const generateAdditionalDetailProductData = ({
  parentId,
  parentCode,
  parentName,
  parentBidexCode,
  brand,
  links,
  attributes,
  contentBaseurl,
  texts,
  media,
  matchedImages,
  relatedAttributes,
  relationships,
  includedArray,
  currentSearch = {},
  isSparePart,
  mergeColorAndFrameAttributes = false,
}) => {
  const matchedImagesUrl = matchedImages.map(image =>
    getImageUrl({
      image,
      contentBaseurl,
    }),
  )
  const mediaThreeSixtyImages = getMediaThreeSixtyImages({ media })
  const matchedThreeSixtyImages = mediaThreeSixtyImages.map(image =>
    getPreviewImage({
      image,
      imageMinSize: IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_DETAIL,
    }),
  )
  const matchedThreeSixtyImagesUrl = matchedThreeSixtyImages.map(image =>
    getImageUrl({
      image,
      contentBaseurl,
    }),
  )

  const mediaDatasheets = getMediaDatasheets({ media })

  const allRelatedProducts = sortRelatedProductsByItemSequence({
    products: getRelatedProducts({
      relationships,
      includedArray,
      itemType: API_CONSTANTS.AIMEOS.TYPE.PRODUCT,
      listType: API_CONSTANTS.AIMEOS.PRODUCTS.TYPE.DEFAULT,
    }),
  })

  const relatedProducts = allRelatedProducts.filter(
    p => p[API_CONSTANTS.ATTRIBUTES][API_CONSTANTS.AIMEOS.PRODUCTS.ATTRIBUTE.TYPE] === 'default',
  )

  // const accessoryProducts = allRelatedProducts.filter(
  //   p =>
  //     p[API_CONSTANTS.ATTRIBUTES][API_CONSTANTS.AIMEOS.PRODUCTS.ATTRIBUTE.TYPE] ===
  //     'bought-together',
  // )
  const accessoryProducts = getRelatedProducts({
    relationships,
    includedArray,
    itemType: API_CONSTANTS.AIMEOS.TYPE.PRODUCT,
    listType: API_CONSTANTS.AIMEOS.PRODUCTS.TYPE.BOUGHT_TOGETHER,
  })

  const selectSearchAttributes = getSearchHits({
    relatedAttributes,
    currentSearch,
  })

  const relatedSpareparts = getRelatedProducts({
    relationships,
    includedArray,
    type: API_CONSTANTS.AIMEOS.TYPE.SPAREPARTS,
    itemType: API_CONSTANTS.AIMEOS.TYPE.PRODUCT,
  })

  const relatedProductData = relatedProducts.map(product =>
    generateAdditionalVariantProductData({
      ...product,
      parentId,
      parentCode,
      parentName,
      parentBidexCode,
      links,
      includedArray,
      contentBaseurl,
      currentSearch,
      selectSearchAttributes,
      addAllImagesWithDefaultSize: IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_DETAIL,
      mergeColorAndFrameAttributes,
    }),
  )

  const accessoryProductData = accessoryProducts.map(product =>
    generateAdditionalVariantProductData({
      ...product,
      parentId,
      parentCode,
      parentName,
      parentBidexCode,
      links,
      includedArray,
      contentBaseurl,
      currentSearch,
      selectSearchAttributes,
      mergeColorAndFrameAttributes,
    }),
  )

  const relatedSparepartsData = relatedSpareparts.map(product =>
    generateAdditionalVariantProductData({
      ...product,
      bikeBrand: isSparePart ? undefined : brand,
      links,
      includedArray,
      contentBaseurl,
      isSparePart: true,
      addAllImagesWithDefaultSize: IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_DETAIL,
    }),
  )

  const productType = getProductType({ attributes })

  const sparepartsSelectIdsString = get(attributes, 'product.sparepartselectids', '')

  return {
    code: getCode({ attributes }),
    config: getConfig({ attributes }),
    label: getLabel({ attributes }),
    productType,
    status: getStatus({ attributes }),
    uspTexts: getUspTexts({ texts }),
    sparepartsSelectIds:
      sparepartsSelectIdsString === '' ? [] : sparepartsSelectIdsString.split(','),
    parentId: get(attributes, 'product.parentid') || parentId,
    parentName: get(attributes, 'product.parentname') || parentName,
    parentCode: get(attributes, 'product.parentcode') || parentCode,
    matchedImagesUrl,
    matchedThreeSixtyImagesUrl,
    datasheets: getDatasheets({ mediaDatasheets, contentBaseurl, name }),
    relatedProducts: relatedProductData,
    accessoryProducts: accessoryProductData,
    relatedSpareparts: relatedSparepartsData,
    specsAttributesFormatted: mergeWith(
      Object.entries(
        cloneDeep(
          getProductSpecsAttributesFormatted({
            attributes: relatedAttributes,
            includedArray,
          }),
        ),
      ).reduce((acc, [key, value]) => {
        acc[key] = isArray(value) ? value : [value]
        return acc
      }, {}),
      getVariantSpecsAttributesFormatted({ relatedProducts, includedArray }),
      mergeValuesToArrays,
    ),
  }
}

export const generateBaseProductData = ({
  id,
  parentId,
  parentCode,
  parentName,
  parentBidexCode,
  type,
  links,
  attributes,
  relationships,
  relatedAttributes,
  includedArray,
  imageMinSize,
  contentBaseurl,
  media,
  texts,
  addAllImagesWithDefaultSize,
  variantType = [],
  mergeColorAndFrameAttributes = false,
} = {}) => {
  const name = getName({ texts, attributes })

  const relatedProductProperties = getRelatedProductProperties({
    relationships,
    includedArray,
  })

  const itemType = getProductItemType({ relatedAttributes })

  const articleType = getArticleType([...relatedAttributes, ...relatedProductProperties])

  const mediaImages = getMediaImages({ media })
  const matchedImages = mediaImages.map(image =>
    getPreviewImage({
      image,
      imageMinSize,
    }),
  )
  const allImages = addAllImagesWithDefaultSize
    ? mediaImages.map(image =>
        getImageUrl({
          image: getPreviewImage({
            image,
            addAllImagesWithDefaultSize,
          }),
          contentBaseurl,
        }),
      )
    : []

  const firstImage = head(matchedImages)

  const allPrices = getPrices({ relationships, includedArray })
  const prices = relevantPricesSorted({
    prices: allPrices,
  })

  const firstPriceItem = head(prices)

  const clientPrices = compact(
    allPrices.map(priceItem => {
      const relationGroups = priceItem?.relationships?.['customer/group']?.data || []
      const groups = compact(
        (
          priceItem?.attributes?.['customer/group'] ||
          relationGroups.map(grp => grp.attributes) ||
          []
        ).map(group => get(DP_ID_TO_ROLE, group['price.lists.refid'])),
      )
      if (!groups || !groups.length) {
        return undefined
      }
      // TODO: Brand Split: split kann failen wenn gruppe nicht in DP_ID_TO_ROLE
      const realClients = groups.map(group => group.split('.')?.[1])
      const clientsWithSub = unzip(realClients.map(c => c.split('-')))
      const hasSale = get(clientsWithSub, 1, []).includes('sale')
      const clients = head(clientsWithSub)
      const realClient = head(realClients)
      const client = head(clients)

      const zzcaConfig = get(priceItem, [
        API_CONSTANTS.ATTRIBUTES,
        API_CONSTANTS.AIMEOS.PRODUCTS.RELATIONSHIP.ATTRIBUTE.CONFIG,
        API_CONSTANTS.AIMEOS.PRODUCTS.RELATIONSHIP.CONFIG.ZZCA,
      ])

      const zzca = zzcaConfig ? parseFloat(zzcaConfig) : zzcaConfig

      const discount = get(priceItem, [
        API_CONSTANTS.ATTRIBUTES,
        API_CONSTANTS.AIMEOS.PRODUCTS.RELATIONSHIP.ATTRIBUTE.CONFIG,
        API_CONSTANTS.AIMEOS.PRODUCTS.RELATIONSHIP.CONFIG.DISCOUNT,
      ])

      const soldOutSelling =
        articleType === FILTER_CONSTANTS.ARTICLETYPE_CODE.BIKE &&
        !!COMPANY_CODE_TO_OVERSELLING?.[CLIENT_TO_COMPANY_CODE?.[client]]

      const isOutlet = variantType.includes(FILTER_CONSTANTS.SCOPE.OUTLET)

      const value = getPriceValue({ priceItem })
      return {
        currency: getPriceCurrency({ priceItem }),
        value,
        zzca,
        soldOutSelling,
        discount: discount ? parseFloat(discount) : discount,
        sort: get(
          priceItem,
          [API_CONSTANTS.ATTRIBUTES, API_CONSTANTS.AIMEOS.PRODUCTS.RELATIONSHIP.ATTRIBUTE.POSITION],
          999,
        ),
        groups,
        realClient,
        realClients,
        hasSale,
        isOutlet,
        client,
        clients,
        brands: clients.map(client =>
          get(COMPANY_CODE_TO_BRAND, get(CLIENT_TO_COMPANY_CODE, client)),
        ),
      }
    }),
  )

  const hasSoldOutSelling = !isUndefined(clientPrices.find(p => p.soldOutSelling))

  const groupPrices = clientPrices.reduce((acc, priceItem) => {
    // !! If there is a price with soldOutSelling we filter out all other clients
    // !! because we don’t support different soldOutSelling values at the moment
    if (hasSoldOutSelling && !priceItem.soldOutSelling) {
      return acc
    }

    const realClient = head(priceItem.client.split('-'))
    if (!has(acc, realClient)) {
      acc[realClient] = []
    }
    acc[realClient].push(priceItem)
    if (acc[realClient].length > 1) {
      acc[realClient] = sortBy(acc[realClient], 'value')
    }
    return acc
  }, {})

  const productType = getProductType({ attributes })
  const productNameInternal = getProductNameInternal(relatedAttributes)

  const archiveItem = isArchiveItem([...relatedAttributes, ...relatedProductProperties])
  const category = getCategory({
    attributes: [...relatedAttributes, ...relatedProductProperties],
    included: includedArray,
  })
  const basketAttributes = getBasketAttributes({ relatedAttributes })
  const productSortAndFilteringProps = getProductSortAndFilteringProps({
    relatedProductProperties,
    relatedAttributes,
  })
  // console.log({ code: getCode({ attributes }), productSortAndFilteringProps })
  const productScope =
    articleType === BIKE_ATTRIBUTES.ARTICLE_TYPE.SPAREPART
      ? FILTER_CONSTANTS.SCOPE.SERVICE
      : articleType === BIKE_ATTRIBUTES.ARTICLE_TYPE.BIKE
      ? FILTER_CONSTANTS.SCOPE.BIKES
      : articleType === BIKE_ATTRIBUTES.ARTICLE_TYPE.ACCESSORY
      ? FILTER_CONSTANTS.SCOPE.ACCESSORIES
      : FILTER_CONSTANTS.SCOPE.ARCHIVE
  const productBidexCode =
    getProductBidexCode([...relatedAttributes, ...relatedProductProperties]) ||
    get(attributes, 'product.parentbidexcode') ||
    parentBidexCode

  const isSparepartArticleType = articleType === BIKE_ATTRIBUTES.ARTICLE_TYPE.SPAREPART

  const selectVariantAttributes = getFormattedSelectVariantAttributes(
    relatedAttributes,
    includedArray,
  )

  let baseData = {
    contentBaseurl,
    ...productSortAndFilteringProps,
    ...getLinkProps({ links }),
    id,
    productBidexCode,
    productNameInternal,
    type,
    productType,
    productScope,
    articleType,
    isSparePart: isSparepartArticleType,
    isSparepartArticleType,
    itemType,
    code: getCode({ attributes }),
    productId: getProductId({ attributes }),
    parentId: get(attributes, 'product.parentid') || parentId,
    parentName: get(attributes, 'product.parentname') || parentName,
    parentCode: get(attributes, 'product.parentcode') || parentCode,
    name,
    category,
    brand: getBrand([...relatedAttributes, ...relatedProductProperties]),
    archiveItem,
    fallbackImage: FallbackImage,
    matchedImages,
    allImages,
    matchedFirstImageUrl: firstImage
      ? getImageUrl({
          image: firstImage,
          contentBaseurl,
        })
      : undefined,
    matchedFirstImageSize: firstImage ? getImageSize({ image: firstImage }) : undefined,
    explodedDrawings: getMediaExplodedDrawing({ media, contentBaseurl }),
    hideProduct: shouldHideProduct({ firstPriceItem, archiveItem }),
    firstPriceCurrency: getPriceCurrency({ priceItem: firstPriceItem }),
    firstPrice: getPriceValue({ priceItem: firstPriceItem }),
    groupPrices,
    hasSoldOutSelling,
    basketAttributes,
    selectVariantAttributes,
  }

  if (mergeColorAndFrameAttributes) {
    const frameSizeAttributes = getFormattedFrameSizeAttributes(relatedAttributes, includedArray)
    const frameSize = getFrameSize(relatedAttributes)
    const frameSizeCm = getFrameSizeCm(relatedAttributes)
    const productSeason = getProductSeason(relatedAttributes)
    const colorAttributes = getFormattedColorAttributes(relatedAttributes, includedArray)
    const frameType = getFrameType(relatedAttributes, includedArray)
    const wheelSizeInch = getWheelSizeInch(relatedAttributes, includedArray)

    baseData = {
      ...baseData,
      frameSizeAttributes,
      frameSize,
      frameSizeCm,
      frameType,
      wheelSizeInch,
      colorAttributes,
      productSeason,
    }
  }

  return baseData
}

export const generateBasketAttributes = ({ apiData } = {}) => {
  const relationships = get(apiData, [API_CONSTANTS.DATA, 0, API_CONSTANTS.RELATIONSHIPS], {})
  const includedArray = get(apiData, API_CONSTANTS.INCLUDED, [])
  const relatedAttributes = getRelatedAttributes({ relationships, includedArray })
  return getBasketAttributes({ relatedAttributes })
}

export const generateBasketAttributesFromBasketinfo = ({ apiData = [] } = {}) => {
  const data = Array.isArray(apiData) ? apiData : []
  return data.reduce(
    (
      result,
      {
        id,
        ArticleType = '',
        basketAttributes = [],
        availabilities = [],
        groupPrices = {},
        hasSoldOutSelling = false,
        upgradeAttributes = {},
      },
    ) => {
      result[id] = {
        ArticleType,
        availabilities,
        groupPrices,
        hasSoldOutSelling,
        upgradeAttributes,
      }
      result[id].basketAttributes = basketAttributes.reduce((attrs, attr) => {
        attrs[get(attr, API_CONSTANTS.AIMEOS.ATTRIBUTE.ATTRIBUTE.TYPE)] = get(
          attr,
          API_CONSTANTS.AIMEOS.ATTRIBUTE.ATTRIBUTE.ID,
        )

        return attrs
      }, {})
      return result
    },
    {},
  )
}

export const generateProductData = ({
  productDetailData = false,
  variantDetailData = false,
  mergeColorAndFrameAttributes = false,
  apiData,
  currentSearch,
}) => {
  const meta = get(apiData, API_CONSTANTS.META.TYPE, {})
  const contentBaseurl = getContentBaseurl(meta)
  const data = getDataArray(get(apiData, API_CONSTANTS.DATA, []))
  const attributeCounts = get(apiData, 'attributeCounts', {})
  const catalogCounts = get(apiData, 'catalogCounts', {})
  const includedArray = get(apiData, API_CONSTANTS.INCLUDED, [])

  return {
    meta,
    attributeCounts,
    catalogCounts,
    data: data.map(({ id, type, links, attributes, relationships } = {}) => {
      const imageMinSize = productDetailData
        ? IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_DETAIL
        : IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_LIST

      const productTypeIsDefault =
        getProductType({ attributes }) === API_CONSTANTS.AIMEOS.PRODUCTS.TYPE.DEFAULT

      if ((!productDetailData && productTypeIsDefault) || variantDetailData) {
        return generateAdditionalVariantProductData({
          id,
          type,
          links,
          attributes,
          relationships,
          includedArray,
          contentBaseurl,
          addAllImagesWithDefaultSize: IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_DETAIL,
        })
      }

      const texts = getTexts({ relationships, includedArray })
      const media = getMedia({ relationships, includedArray })
      const relatedAttributes = getRelatedAttributes({ relationships, includedArray })

      let baseData = generateBaseProductData({
        id,
        type,
        links,
        attributes,
        relationships,
        relatedAttributes,
        includedArray,
        imageMinSize,
        contentBaseurl,
        media,
        texts,
        mergeColorAndFrameAttributes,
      })

      return {
        ...baseData,
        ...(productDetailData
          ? generateAdditionalDetailProductData({
              parentId: id,
              parentCode: get(baseData, 'code'),
              parentName: get(baseData, 'name'),
              parentBidexCode: get(baseData, 'productBidexCode'),
              brand: get(baseData, 'brand'),
              links,
              attributes,
              contentBaseurl,
              relationships,
              relatedAttributes,
              includedArray,
              texts,
              media,
              matchedImages: baseData.matchedImages,
              currentSearch,
              isSparePart: get(baseData, 'isSparePart'),
              mergeColorAndFrameAttributes,
            })
          : {}),
      }
    }),
  }
}

export const generateCompareProductData = ({ apiData }) => {
  const meta = get(apiData, API_CONSTANTS.META.TYPE, {})
  const data = getDataArray(get(apiData, API_CONSTANTS.DATA, []))
  const contentBaseurl = getContentBaseurl(meta)

  const normalizedData = data.map(({ id, type, scope, links, product, relationships } = {}) => {
    const relationshipData = get(relationships, [API_CONSTANTS.COMPARE, API_CONSTANTS.DATA])
    const attributeData = get(relationshipData, API_CONSTANTS.PRODUCTS.ATTRIBUTE)
    const itemScopes = get(relationshipData, API_CONSTANTS.PRODUCTS.ITEM_SCOPES) // Todo: Check if needed/replaced
    const priceData = get(relationshipData, API_CONSTANTS.PRODUCTS.PRICE)
    const mediaData = get(relationshipData, API_CONSTANTS.PRODUCTS.MEDIA)
    const mediaPreviews = mediaData?.previews ?? {}

    const size = IMAGE_CONSTANTS.IMAGE_MIN_SIZE.PRODUCT_LIST
    const previewImage = getCompareMediaPreview({ mediaPreviews, size })
    const imageUrl = previewImage ? get(previewImage, 'url') : undefined

    // Normalize Values in Attribute Data
    Object.keys(attributeData).map(key => {
      // If Group Config contains this key, additional logic for normalization
      if (Object.keys(compareProductGroupConfig).includes(key)) {
        const nestedData = attributeData[key]
        const config = compareProductGroupConfig[key]

        // Iterate over all fields for this group, write into array of joined values
        // Resulting in an array of joined groups
        // Write into new field, whose key is defined in config.target
        attributeData[config.target] = config.fields.map(field => {
          // Map values, Remove undefineds from array
          const arr = compact(map(nestedData[field], 'content'))

          // Only join Values if it is not an empty array, else we get an empty string
          return arr.length ? arr.join(', ') : null
        })
        // Remove now unused key from backend
        delete attributeData[key]
      } else {
        // Replace normalized Values for key in attribute Data
        attributeData[key] = [map(attributeData[key], 'content')?.join(', ')]
      }
    })

    return {
      id,
      type,
      links,
      contentBaseurl,
      itemScopes,
      scope,
      isArchive: itemScopes.includes(FILTER_CONSTANTS.SCOPE.ARCHIVE),
      isOutletProduct: itemScopes.includes(FILTER_CONSTANTS.SCOPE.OUTLET),
      isCurrent: itemScopes.includes(FILTER_CONSTANTS.SCOPE.CURRENT),
      brand: attributeData['ItemBrand']?.join(''),
      name: product['product.label'],
      ...product,
      ...attributeData,
      matchedFirstImageUrl: imageUrl,
      matchedFirstImagePreviews: mediaData.previews,
      firstPrice: Number(priceData.value),
      firstPriceCurrency: priceData.currency,
    }
  })

  return {
    meta,
    data: normalizedData,
  }
}
